import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const linkClassName = 'group flex flex-col items-center justify-center';
const innerClassName = 'py-2 flex flex-col items-center justify-center gap-2';
const labelClassName = 'text-xs font-mono-recursive';
const iconContainerBaseClassName =
  'transition ease-in-out duration-250 flex items-center justify-center w-9 h-9 rounded-xl';
const iconBaseClassName = 'icon flex-shrink-0 w-5 h-5';

function getIconContainerClassName(active: boolean) {
  return active
    ? `${iconContainerBaseClassName} bg-gradient-to-br from-blue-700 to-violet-800`
    : `${iconContainerBaseClassName} group-hover:bg-white/50 dark:group-hover:bg-white/10`;
}

function getIconClassName(active: boolean) {
  return active ? `${iconBaseClassName} text-white` : iconBaseClassName;
}

type SidebarNavItemProps = {
  to?: string;
  href?: string;
  label?: string;
  active?: boolean;
  external?: boolean;
  icon: (active: boolean) => React.ReactNode;
};

function SidebarNavItem(props: SidebarNavItemProps) {
  const { label, icon, active = false } = props;

  const content = (
    <div className={innerClassName}>
      <div className={getIconContainerClassName(active)}>{icon(active)}</div>
      {label && <p className={labelClassName}>{label}</p>}
    </div>
  );

  if (props.href) {
    return (
      <a href={props.href} className={linkClassName} target="_self" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  if (props.external && props.to) {
    return (
      <Link to={props.to} className={linkClassName} target="_blank" rel="noopener noreferrer">
        {content}
      </Link>
    );
  }

  return (
    <Link to={props.to ?? '/'} className={linkClassName}>
      {content}
    </Link>
  );
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="text-zinc-800 dark:text-zinc-200 hidden md:block">
        <div className="relative bg-transparent w-24"></div>
        <div className="fixed inset-y-0 z-10 hidden h-svh md:flex left-0 p-2 w-24">
          <div className="flex h-full w-full flex-col gap-2 rounded-xl">
            <div className="flex flex-col p-2">
              <Link className="logo flex items-center justify-center text-xl transition duration-300" to="/">
                <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M20 40C8.9543 40 0 31.0457 0 20S8.9543 0 20 0s20 8.9543 20 20-8.9543 20-20 20zm.7408-11.9472c.0146 2.2962 5.581 2.7797 8.4101 1.9174 3.8157-1.1629 5.4-3.3432 4.7995-6.5593-.5361-2.8716-7.2385-4.7076-6.7548-7.6109.4837-2.9032 5.4605-3.1658 5.4038-5.0344-.0334-1.099-.8676-1.3778-3.1357-.935-2.268.443-5.3373 3.1494-5.9375 4.9525-1.2983 3.9004 3.5193 5.892 3.5193 8.6278 0 2.7358-6.3214 2.0175-6.3047 4.6419zm-6.6431-8.6471c.2184-3.346 4.2932-2.887 4.4195-6.0521.1009-2.5258-5.7326-1.4675-8.6434.2641s-4.3303 3.9864-3.859 6.7462c.706 4.1341 8.447 10.0832 10.8449 8.1486 2.398-1.9345-2.9805-5.761-2.762-9.1068z"
                    fill="currentColor" fillRule="evenodd" />
                </svg>
              </Link>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto">
              <div className="relative flex w-full min-w-0 flex-col p-2">
                <SidebarNavItem
                  to="/"
                  label="Home"
                  active={isActive('/')}
                  icon={(active) => (
                    <svg className={getIconClassName(active)} fill="none" viewBox="0 0 16 16">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1"
                        d="m12 8.667-.917-4.582a.667.667 0 0 0-.497-.518l-8.43-2.214a.667.667 0 0 0-.804.804l2.215 8.43a.667.667 0 0 0 .517.497l4.583.917M1.533 1.534 6.39 6.391m4.081 7.805a.667.667 0 0 1-.943 0l-1.057-1.057a.667.667 0 0 1 0-.943l3.724-3.724a.667.667 0 0 1 .943 0l1.057 1.057a.667.667 0 0 1 0 .943l-3.724 3.724ZM8.667 7.334a1.333 1.333 0 1 1-2.667 0 1.333 1.333 0 0 1 2.667 0Z" />
                    </svg>
                  )}
                />
                <SidebarNavItem
                  to="/about"
                  label="About"
                  active={isActive('/about')}
                  icon={(active) => (
                    <svg className={getIconClassName(active)} fill="none" viewBox="0 0 20 20">
                      <path fill="currentColor" d="M9.583 10.833a.417.417 0 1 0 0-.834.417.417 0 0 0 0 .834Z" />
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                        d="m9.916 10.082 3.762-3.761m0 0c.313-.313.488-.736.489-1.178v-1.12c0-.442.175-.866.488-1.179l.928-.928a.833.833 0 0 1 1.167 0l1.333 1.333a.833.833 0 0 1 0 1.167l-.928.928a1.667 1.667 0 0 1-1.178.489h-1.12c-.442 0-.866.175-1.179.488ZM5 13.333l1.667 1.666m.166-6.75c.417-.916 1.333-1.583 2.333-1.583a4.126 4.126 0 0 1 4.167 4.167c0 1-.667 1.916-1.583 2.333l-.75.333a1.667 1.667 0 0 0-1 1.5 3.333 3.333 0 0 1-3.333 3.334c-2.75 0-5-2.25-5-5A3.333 3.333 0 0 1 5 9.999a1.667 1.667 0 0 0 1.5-1l.333-.75ZM10 10.416a.417.417 0 1 1-.833 0 .417.417 0 0 1 .833 0Z" />
                    </svg>
                  )}
                />
                <SidebarNavItem
                  href="mailto:chris@christophersamuels.com?subject=Portfolio Inquiry&body=Hi Christopher,%0D%0A%0D%0AI came across your portfolio and would like to discuss..."
                  label="Contact"
                  icon={(active) => (
                    <svg className={getIconClassName(active)} fill="none" viewBox="0 0 16 16">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1"
                        d="m14.667 4.666-5.98 3.8a1.293 1.293 0 0 1-1.373 0l-5.98-3.8m1.333-2h10.666c.737 0 1.334.597 1.334 1.333v8c0 .737-.597 1.334-1.333 1.334H2.667a1.333 1.333 0 0 1-1.333-1.334V4c0-.736.596-1.333 1.333-1.333Z" />
                    </svg>
                  )}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 p-2">
              <ul className="flex w-full min-w-0 flex-col gap-1">
                <li className="group/menu-item relative">
                  <SidebarNavItem
                    to="https://www.linkedin.com/in/samuelschristopher/"
                    external
                    icon={(active) => (
                      <svg className={getIconClassName(active)} fill="none" viewBox="0 0 20 20">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                          d="M13.333 6.666a5 5 0 0 1 5 5v5.833H15v-5.833a1.667 1.667 0 0 0-3.334 0v5.833H8.333v-5.833a5 5 0 0 1 5-5ZM5 7.5H1.667v10H5v-10ZM3.333 5a1.667 1.667 0 1 0 0-3.334 1.667 1.667 0 0 0 0 3.333Z" />
                      </svg>
                    )}
                  />
                </li>
                <li className="group/menu-item relative">
                  <SidebarNavItem
                    to="https://codepen.io/chrissamuels"
                    external
                    icon={(active) => (
                      <svg className={getIconClassName(active)} fill="none" viewBox="0 0 20 20">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                          d="M2 12.69 10 18m-8-5.31V7.31m0 5.38 8-5.38M10 18l8-5.31M10 18v-5.31m8 0V7.31m0 5.38-8-5.38m8 0L10 2m8 5.31-8 5.308L2 7.31M10 2 2 7.31M10 2v5.31" />
                      </svg>
                    )}
                  />
                </li>
              </ul>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
